// src/App.tsx
import React, { Suspense, useState } from "react";
import "./App.css";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { setNavigate } from "./services/native/navigationRef";
import Header from "./components/layouts/headers/Header";
import { NotificationProvider } from "./components/shared/contexts/NotificationContext";
import FirebaseNotificationsProvider from "./components/shared/contexts/FirebaseNotificationsProvider";
import { SwallowTipProvider } from "./components/shared/SwallowTip";
import { BlockedUsersProvider } from "./components/shared/contexts/BlockedUsersContext";
import RequireAuth from "./routes/RequireAuth";
import RequireTrust from "./routes/RequireTrust";
import AppConfig from "./config/config";
import { CATALOG_IMPORT_REVIEW_ROUTE } from "./constants/CatalogImportRoutes";
import NotificationPopup from "./components/layouts/headers/childComponents/NotificationPopup";
import LoadingOverlay from "./components/layouts/headers/childComponents/LoadingOverlay";
import WorkInProgress from "./components/layouts/headers/childComponents/WorkInProgress";
import LoadUserData from "./routes/LoadUserData";
import BottomNavBar from "./components/layouts/headers/BottomNavBar";
import { AppDispatch, RootState } from "./dataManagement/uiDb/UiDb";
import { useDispatch, useSelector } from "react-redux";
import { fetchUiConfigurations } from "./services/thunks/AdminThunk";
import VersionCheckPopup from "./components/shared/VersionCheckPopup/VersionCheckPopup";
import { fetchProfile } from "./services/thunks/ProfileThunk";
import { fetchYetToRespondCount } from "./services/thunks/MyMessagesThunk";
import HotspotBanner from "./components/layouts/headers/childComponents/HotspotBanner";
import genericUtility from "./services/utility/GenericUtility";
import PlaceSearch from "./components/shared/PlaceSearch/PlaceSearch";
import useAutoLogout from "./hooks/useAutoLogout";

// ── Lazy routes ──────────────────────────────────────────────────────────
// One chunk per route, fetched on first navigation to it. The QR entry
// points (ProductDetailsV2, BusinessListings, BusinessCollectionPage,
// ListingCollectionPage, DineIn) are lazy as well, but main.tsx preloads the
// one matching the landing URL so a scan doesn't wait on an extra round trip.
import {
  loadProductDetailsV2,
  loadBusinessListings,
  loadDineIn,
  loadBusinessCollectionPage,
  loadListingCollectionPage,
} from "./routes/routeChunks";
import BusinessPageSkeleton from "./components/pages/businessListings/BusinessPageSkeleton";
import RouteLoadingFallback from "./components/shared/RouteLoadingFallback/RouteLoadingFallback";

// QR entry pages (see routes/routeChunks.ts).
const ProductDetailsV2 = React.lazy(loadProductDetailsV2);
const BusinessListings = React.lazy(loadBusinessListings);
const DineIn = React.lazy(loadDineIn);
const BusinessCollectionPage = React.lazy(loadBusinessCollectionPage);
const ListingCollectionPage = React.lazy(loadListingCollectionPage);
const ProductDetails = React.lazy(() => import("./components/features/Product/ProductDetails/ProductDetails"));
const SlotCalendarLegacyRedirect = React.lazy(() => import("./components/features/Product/ProductDetailsV2/childComponents/SlotAvailabilitySection/SlotCalendarLegacyRedirect"));
const Home = React.lazy(() => import("./components/pages/home/home"));
// Login/Signup/ForgotPassword modal -- only fetched the first time it is opened.
const Auth = React.lazy(() => import("./components/pages/auth/Auth"));
const AuthForm = React.lazy(() => import("./components/pages/auth/AuthForm"));
const MyListings = React.lazy(() => import("./components/pages/myListings/MyListings"));
const MyMessages = React.lazy(() => import("./components/pages/messages/Messages"));
const MyProfile = React.lazy(() => import("./components/pages/profile/Profile"));
const TrustForm = React.lazy(() => import("./components/pages/auth/TrustForm"));
const SearchPage = React.lazy(() => import("./components/pages/search/SearchPage"));
const LandingPage = React.lazy(() => import("./components/pages/auth/LandingPage"));
const NineFindsLanding = React.lazy(() => import("./components/pages/landing/NineFindsLanding"));
const NineFinds = React.lazy(() => import("./components/features/Product/NineFinds/NineFinds"));
const OurStory = React.lazy(() => import("./components/pages/OurStory/OurStory"));
const PrivacyPolicy = React.lazy(() => import("./components/pages/PrivacyPolicy/PrivacyPolicy"));
const ContactUs = React.lazy(() => import("./components/pages/ContactUs/ContactUs"));
const PricingPage = React.lazy(() => import("./components/pages/Pricing/PricingPage"));
const Terms = React.lazy(() => import("./components/pages/Terms/Terms"));
const RefundsPolicy = React.lazy(() => import("./components/pages/Refunds/RefundsPolicy"));
const ShippingPolicy = React.lazy(() => import("./components/pages/Shipping/ShippingPolicy"));
const UserListings = React.lazy(() => import("./components/pages/userListings/UserListings"));
const ExternalListingImport = React.lazy(() => import("./components/pages/externalListingImports/ExternalListingImport"));
const CartPage = React.lazy(() => import("./components/pages/cart/CartPage"));
const SprintActivity = React.lazy(() => import("./components/pages/sprint-activity"));
const SupportPage = React.lazy(() => import("./components/pages/Support/SupportPage"));
// Lazy so three.js stays out of the main bundle — it loads only on /monkey-preview.
const MonkeyPreview = React.lazy(() => import("./components/pages/monkeyPreview/MonkeyPreview"));
const CatalogImportReviewPage = React.lazy(() => import("./components/features/CatalogImport/review/CatalogImportReviewPage"));

const App: React.FC = () => {
  useAutoLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const dispatch: AppDispatch = useDispatch();

  // Expose SPA navigation to the native bridge (window.NineFindsNative.navigate).
  React.useEffect(() => {
    setNavigate(navigate);
    return () => setNavigate(null);
  }, [navigate]);

  // React Router doesn't reset scroll position on navigation — without this,
  // a page can open mid-scroll if the previous page was scrolled down.
  // Skipped on POP (back/forward) so it doesn't fight the browser's native
  // scrollRestoration, which ProductDetailsUtils.scrollToTop/restoreScrollRestoration
  // already manage deliberately to restore list-scroll position on back-nav.
  // Hash links (e.g. /privacy-policy#grievance) scroll to that section instead.
  React.useEffect(() => {
    if (navigationType === "POP") return;
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, navigationType]);
  const { loadingMap, userPreferences } = useSelector((state: RootState) => state.profile);
  const reduxLocation = useSelector((state: RootState) => state.general.UserLocation);
  const isAuthOpen = useSelector((state: RootState) => state.general.isAuthOpen);
  const [isMandatoryLocationModalOpen, setIsMandatoryLocationModalOpen] = useState(false);

  // Fetch initial data on mount
  React.useEffect(() => {
    dispatch(fetchUiConfigurations());
    // Anonymous visitors (most QR scans) have no profile to fetch.
    if (genericUtility.getCookie("token")) {
      dispatch(fetchProfile());
    }
    // Seed the "yet to respond" badge from the authoritative count (logged-in
    // users only; the endpoint requires auth).
    if (genericUtility.getCookie("token")) {
      dispatch(fetchYetToRespondCount());
    }
  }, []);

  // ── Location startup flow ──
  React.useEffect(() => {
    if (loadingMap.fetchProfile) return;

    const pathname = location.pathname;
    const isExceptionRoute =
      pathname.startsWith('/product-details/') ||
      pathname.startsWith('/listings/') ||
      pathname.startsWith('/business/') ||
      // A shared/scanned collection link shouldn't be gated behind the
      // mandatory location picker.
      pathname.startsWith('/collection/') ||
      pathname.startsWith('/listing-collection/') ||
      pathname.startsWith('/cart') ||
      // Table QR scans have nothing to do with location — don't block the
      // flow with the mandatory location picker.
      pathname.startsWith('/dine-in/');

    if (isExceptionRoute) {
      genericUtility.resolveStartupLocation(dispatch, () => {}, userPreferences);
      setIsMandatoryLocationModalOpen(false);
    } else {
      genericUtility.resolveStartupLocation(dispatch, setIsMandatoryLocationModalOpen, userPreferences);
    }
  }, [dispatch, loadingMap.fetchProfile, userPreferences, location.pathname, reduxLocation]);

  // Called only when the user picks a location inside the mandatory modal
  const handleMandatoryLocationSelected = () => {
    genericUtility.handleLocationSelected(dispatch, setIsMandatoryLocationModalOpen);
  };

  return (
    <>
      <BlockedUsersProvider>
      <NotificationProvider>
      <SwallowTipProvider>
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/landing" element={<NineFindsLanding />} />
          <Route path="/get-started" element={<LandingPage />} />
          <Route path="/signup" element={<AuthForm />} />

          <Route path="/search" element={<SearchPage />} />
          {/* Public: shared/scanned collection links must open for logged-out users. */}
          <Route path="/collection/:id" element={<BusinessCollectionPage />} />
          <Route path="/listing-collection/:id" element={<ListingCollectionPage />} />
          <Route path="/external-listing" element={<ExternalListingImport />} />
          <Route path="/dine-in/:businessId/:tableNumber" element={<DineIn />} />
          {/* Scratch page for the DineIn celebration character — not linked from anywhere. */}
          <Route
            path="/monkey-preview"
            element={
              <React.Suspense fallback={null}>
                <MonkeyPreview />
              </React.Suspense>
            }
          />
          <Route element={<LoadUserData />}>
            <Route
              element={
                <>
                  <BottomNavBar /> <Outlet />
                </>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/welcome" element={<Home />} />
              <Route path="/home" element={<Home />} />
            </Route>
            {/* Visited-profile pages render nav-less (focused view, own back button) */}
            <Route path="listings/:id" element={<UserListings />} />
            <Route
              path="business/:id"
              element={
                <Suspense fallback={<BusinessPageSkeleton />}>
                  <BusinessListings />
                </Suspense>
              }
            />
          </Route>
          <Route path="/cart" element={<CartPage />} />
          <Route path="/product-details-v0/:id" element={<ProductDetails />} />
          <Route path="/product-details/:id" element={<ProductDetailsV2 />} />
          <Route path="/product-details/:id/availability/manage" element={<SlotCalendarLegacyRedirect panel="manage" />} />
          <Route path="/product-details/:id/availability/setup" element={<SlotCalendarLegacyRedirect panel="setup" />} />
          <Route path="/ninefinds" element={<><NineFinds /></>} />
          <Route path="/our-story" element={<OurStory />} />
          {/*
            Two paths, one component. "/privacy" is the URL declared to
            Razorpay; "/privacy-policy" is the long-standing internal path
            used by the footer and by /contact's #grievance deep-link. Both
            must keep resolving — do not collapse one into a redirect without
            re-verifying the payment-gateway submission.
          */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refunds" element={<RefundsPolicy />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/sprint-activity/qwerty123456" element={<SprintActivity />} />
          <Route element={<RequireAuth />}>
            <Route path="/validateuser" element={<TrustForm />} />
            <Route element={<RequireTrust />}>
              {AppConfig.featureFlags.enableCatalogImport && (
                <Route path={CATALOG_IMPORT_REVIEW_ROUTE} element={<CatalogImportReviewPage />} />
              )}
              <Route
                element={
                  <>
                    <BottomNavBar /> <Outlet />
                  </>
                }
              >
                <Route path="/my-listings" element={<MyListings />} />
                <Route path="/my-messages" element={<MyMessages />} />
                <Route path="/my-profile" element={<MyProfile />} />
              </Route>
            </Route>
          </Route>
        </Routes>
        </Suspense>
        <FirebaseNotificationsProvider />
        <NotificationPopup />
        <LoadingOverlay />
        <WorkInProgress />
        {isAuthOpen && (
          <Suspense fallback={null}>
            <Auth />
          </Suspense>
        )}
        <VersionCheckPopup />
        <HotspotBanner />
        {/* Mandatory location selector — appears only when no location is set */}
        <PlaceSearch
          isModal={true}
          isOpen={isMandatoryLocationModalOpen}
          onClose={handleMandatoryLocationSelected}
          mandatory={true}
        />
      </SwallowTipProvider>
      </NotificationProvider>
      </BlockedUsersProvider>
    </>
  );
};

export default App;
